import { useCallback, useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useAuth } from "../../context/AuthContext";
import {
  SUBSCRIPTION_TIERS,
  TIER_ORDER,
  formatTierDollars,
  getTier,
  possessiveName,
} from "../../constants/subscriptionTiers";
import { TIER_CATALOG_BY_INDEX } from "../../services/supportTierService";
import {
  cancelCreatorSubscription,
  confirmSubscriptionCreated,
  createSubscriptionPaymentIntent,
} from "../../services/subscriptionService";
import { formatDollarPrice, formatMonthlyPrice } from "../../utils/priceFormat";
import "./TierSubscriptionModal.css";
import "../../Styles/profileThemes.css";

const stripePromiseCache = new Map();

function getStripePromise(publishableKey) {
  const key =
    publishableKey
    || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    || "";
  if (!key) return null;
  if (!stripePromiseCache.has(key)) {
    stripePromiseCache.set(key, loadStripe(key));
  }
  return stripePromiseCache.get(key);
}

function PaymentForm({ email, onReady, onStripeRefs }) {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    onStripeRefs?.({ stripe, elements });
    onReady?.(Boolean(stripe && elements));
  }, [stripe, elements, onReady, onStripeRefs]);

  return (
    <div className="tsm-stripe-mount">
      <PaymentElement
        options={{
          layout: "tabs",
          defaultValues: {
            billingDetails: {
              email: email || undefined,
            },
          },
        }}
      />
    </div>
  );
}

export default function TierSubscriptionModal({
  isOpen,
  creatorName = "Creator",
  creatorId,
  currentSubscriptionTier = null,
  initialTier = null,
  initialTierIndex = null,
  creatorTiers = null,
  autoStartPayment = false,
  profileThemeId = null,
  profileThemeStyle = null,
  onDismiss,
  onSubscribe,
}) {
  const { currentUser, refreshUserProfile } = useAuth();
  const [phase, setPhase] = useState("select"); // select | payment | manage
  const [selectedTier, setSelectedTier] = useState("inner");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [publishableKey, setPublishableKey] = useState(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
  );
  const [paymentReady, setPaymentReady] = useState(false);
  const [stripeRef, setStripeRef] = useState({ stripe: null, elements: null });
  const [autoStartedFor, setAutoStartedFor] = useState(null);

  const displayTiers = useMemo(() => {
    if (Array.isArray(creatorTiers) && creatorTiers.length) {
      return creatorTiers.slice(0, 3).map((t, i) => {
        const id = t.catalogId || TIER_CATALOG_BY_INDEX[i] || "supporter";
        return {
          id,
          name: t.name,
          priceLabel: formatDollarPrice(t.price) || getTier(id)?.priceLabel || "",
          priceMonthly: formatMonthlyPrice(t.price),
          description: t.description || getTier(id)?.description || "",
          features: t.perks?.length ? t.perks : (getTier(id)?.features || []),
          recommended: i === 1 && creatorTiers.length > 1,
          accentIndex: i,
        };
      });
    }
    return TIER_ORDER.map((id) => {
      const t = SUBSCRIPTION_TIERS[id];
      return {
        id: t.id,
        name: t.name,
        priceLabel: t.priceLabel,
        priceMonthly: `${t.priceLabel}/mo`,
        description: t.description,
        features: t.features,
        recommended: Boolean(t.recommended),
        accentIndex: TIER_ORDER.indexOf(id),
      };
    });
  }, [creatorTiers]);

  const selectedDisplay = displayTiers.find((t) => t.id === selectedTier) || displayTiers[0];
  const tier = getTier(selectedTier) || {
    id: selectedTier,
    name: selectedDisplay?.name,
    priceLabel: selectedDisplay?.priceLabel,
    description: selectedDisplay?.description,
    features: selectedDisplay?.features || [],
  };
  const currentTier = currentSubscriptionTier
    ? getTier(currentSubscriptionTier)
    : null;
  const firstName = String(creatorName || "Creator").split(" ")[0];

  useEffect(() => {
    if (!isOpen) {
      setAutoStartedFor(null);
      return;
    }
    setPhase(currentSubscriptionTier ? "manage" : "select");
    let next = initialTier || currentSubscriptionTier || null;
    if (next == null && initialTierIndex != null && displayTiers[initialTierIndex]) {
      next = displayTiers[initialTierIndex].id;
    }
    if (next == null) {
      next = displayTiers.length >= 2 ? displayTiers[1].id : (displayTiers[0]?.id || "inner");
    }
    setSelectedTier(next);
    setBusy(false);
    setError("");
    setClientSecret(null);
    setSubscriptionId(null);
    setPaymentReady(false);
  }, [isOpen, currentSubscriptionTier, initialTier, initialTierIndex, displayTiers]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onDismiss?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, busy, onDismiss]);

  const stripePromise = useMemo(
    () => getStripePromise(publishableKey),
    [publishableKey]
  );

  const handleDismiss = () => {
    if (busy) return;
    onDismiss?.();
  };

  const startPaymentPhase = useCallback(async (tierId = selectedTier) => {
    if (!creatorId) {
      setError("Missing creator. Please refresh and try again.");
      return;
    }
    if (!currentUser) {
      setError("Sign in to subscribe.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const data = await createSubscriptionPaymentIntent({
        creatorId,
        tier: tierId,
        creatorName,
      });

      if (data.publishableKey) setPublishableKey(data.publishableKey);
      setSubscriptionId(data.subscriptionId);

      if (data.alreadyPaid) {
        await confirmSubscriptionCreated({
          subscriptionId: data.subscriptionId,
          creatorId,
          tier: tierId,
          creatorName,
        }).catch(() => null);
        refreshUserProfile?.(currentUser.uid);
        onSubscribe?.({
          action: currentSubscriptionTier ? "upgrade" : "subscribe",
          tier: tierId,
          subscriptionId: data.subscriptionId,
        });
        onDismiss?.();
        return;
      }

      if (!data.clientSecret) {
        throw new Error("Payment could not be started.");
      }

      setClientSecret(data.clientSecret);
      setSelectedTier(tierId);
      setPhase("payment");
    } catch (err) {
      setError(err.message || "Could not start checkout.");
    } finally {
      setBusy(false);
    }
  }, [
    creatorId,
    creatorName,
    currentSubscriptionTier,
    currentUser,
    onDismiss,
    onSubscribe,
    refreshUserProfile,
    selectedTier,
  ]);

  useEffect(() => {
    if (!isOpen || !autoStartPayment || currentSubscriptionTier) return;
    const tierId = initialTier || selectedTier || "inner";
    const token = `${creatorId}:${tierId}`;
    if (autoStartedFor === token) return;
    setAutoStartedFor(token);
    startPaymentPhase(tierId);
  }, [
    autoStartPayment,
    autoStartedFor,
    creatorId,
    currentSubscriptionTier,
    initialTier,
    isOpen,
    selectedTier,
    startPaymentPhase,
  ]);

  const handlePrimary = async () => {
    if (phase === "select" || phase === "manage") {
      if (phase === "manage" && selectedTier === currentSubscriptionTier) {
        setError("Choose a different tier to upgrade, or unsubscribe.");
        return;
      }
      await startPaymentPhase(selectedTier);
      return;
    }

    // Confirm & pay
    const { stripe, elements } = stripeRef;
    if (!stripe || !elements || !subscriptionId) {
      setError("Payment form is still loading.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
          payment_method_data: {
            billing_details: {
              email: currentUser?.email || undefined,
              name: currentUser?.displayName || undefined,
            },
          },
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message || "Payment failed.");
      }

      if (
        paymentIntent
        && paymentIntent.status !== "succeeded"
        && paymentIntent.status !== "processing"
      ) {
        throw new Error(`Payment status: ${paymentIntent.status}`);
      }

      await confirmSubscriptionCreated({
        subscriptionId,
        creatorId,
        tier: selectedTier,
        creatorName,
      });

      refreshUserProfile?.(currentUser.uid);
      onSubscribe?.({
        action: currentSubscriptionTier ? "upgrade" : "subscribe",
        tier: selectedTier,
        subscriptionId,
      });
      onDismiss?.();
    } catch (err) {
      setError(err.message || "Payment failed. You can retry or go back.");
    } finally {
      setBusy(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!creatorId) return;
    setBusy(true);
    setError("");
    try {
      await cancelCreatorSubscription({ creatorId });
      refreshUserProfile?.(currentUser?.uid);
      onSubscribe?.({ action: "unsubscribe", tier: null, creatorId });
      onDismiss?.();
    } catch (err) {
      setError(err.message || "Could not cancel subscription.");
    } finally {
      setBusy(false);
    }
  };

  const handleBack = () => {
    if (busy) return;
    setClientSecret(null);
    setSubscriptionId(null);
    setPaymentReady(false);
    setError("");
    setPhase(currentSubscriptionTier ? "manage" : "select");
  };

  const onPaymentReady = useCallback((ready) => {
    setPaymentReady(ready);
  }, []);

  const onStripeRefs = useCallback((refs) => {
    setStripeRef(refs || { stripe: null, elements: null });
  }, []);

  if (!isOpen) return null;

  const isSameAsCurrent =
    Boolean(currentSubscriptionTier) && selectedTier === currentSubscriptionTier;

  const primaryLabel =
    phase === "payment"
      ? busy
        ? "Processing…"
        : "Confirm & pay"
      : busy
        ? "Preparing…"
        : isSameAsCurrent
          ? `Subscribed to ${tier?.name || "tier"}`
          : currentSubscriptionTier
            ? `Upgrade to ${tier?.name || "tier"}`
            : `Subscribe to ${tier?.name || "tier"}`;

  const primaryDisabled =
    busy
    || !tier
    || (phase === "payment" && (!clientSecret || !paymentReady))
    || (isSameAsCurrent && phase !== "payment");

  return (
    <div
      className={`tsm-overlay tier-${selectedTier} tsm-overlay--sheet`}
      data-profile-theme={profileThemeId || undefined}
      style={profileThemeStyle || undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tsm-headline"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleDismiss();
      }}
    >
      <div className={`tsm-modal glow-${selectedTier}`} data-tier-idx={selectedDisplay?.accentIndex ?? 0}>
        <div className="tsm-header">
          <button
            type="button"
            className="tsm-close"
            aria-label="Close"
            onClick={handleDismiss}
          >
            ×
          </button>
          <div className="tsm-eyebrow">Support this creator</div>
          <div className="tsm-headline" id="tsm-headline">
            Join <em>{possessiveName(creatorName)}</em> circle
          </div>
          <div className="tsm-subtitle">
            Choose a tier to unlock this moment and all future exclusive content from{" "}
            {firstName}.
          </div>
          <div className="tsm-hand">— cancel anytime, no questions asked</div>
        </div>

        <div className="tsm-body">
          {(phase === "select" || phase === "manage") && (
            <div className="tsm-tier-selection">
              {currentTier && (
                <div className="tsm-current-sub">
                  <div className="tsm-current-sub-title">Your current subscription</div>
                  <div className="tsm-current-sub-name">{currentTier.name}</div>
                  <div className="tsm-current-sub-price">
                    {formatTierDollars(currentTier.amountCents)}/month
                  </div>
                  <div className="tsm-action-buttons">
                    <button
                      type="button"
                      className="tsm-upgrade-btn"
                      disabled={busy}
                      onClick={() => {
                        setError("");
                        // Keep on manage/select — user picks a different tier then subscribes
                        const next =
                          selectedTier === currentSubscriptionTier
                            ? selectedTier === "vip"
                              ? "inner"
                              : "vip"
                            : selectedTier;
                        setSelectedTier(next);
                      }}
                    >
                      Upgrade tier
                    </button>
                    <button
                      type="button"
                      className="tsm-unsubscribe-btn"
                      disabled={busy}
                      onClick={handleUnsubscribe}
                    >
                      Unsubscribe
                    </button>
                  </div>
                </div>
              )}

              {displayTiers.map((t) => {
                const selected = selectedTier === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`tsm-tier-card${selected ? " selected" : ""}`}
                    data-tier={t.id}
                    data-tier-idx={t.accentIndex}
                    onClick={() => {
                      setSelectedTier(t.id);
                      setError("");
                    }}
                  >
                    <div className="tsm-tier-check">✓</div>
                    {t.recommended && (
                      <div className="tsm-recommended">Most popular</div>
                    )}
                    <div className="tsm-tier-name">{t.name}</div>
                    <div className="tsm-tier-price">
                      {t.priceLabel}
                      <span className="tsm-tier-price-sub">/month</span>
                    </div>
                    {t.description && (
                      <div className="tsm-tier-description">{t.description}</div>
                    )}
                    <ul className="tsm-tier-features">
                      {(t.features || []).map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>
          )}

          {phase === "payment" && tier && (
            <div className="tsm-payment-phase">
              {currentTier && (
                <div className="tsm-current-sub">
                  <div className="tsm-current-sub-title">Your current subscription</div>
                  <div className="tsm-current-sub-name">{currentTier.name}</div>
                  <div className="tsm-current-sub-price">
                    {formatTierDollars(currentTier.amountCents)}/month
                  </div>
                  <div className="tsm-action-buttons">
                    <button
                      type="button"
                      className="tsm-upgrade-btn"
                      disabled={busy}
                      onClick={handleBack}
                    >
                      Change tier
                    </button>
                    <button
                      type="button"
                      className="tsm-unsubscribe-btn"
                      disabled={busy}
                      onClick={handleUnsubscribe}
                    >
                      Unsubscribe
                    </button>
                  </div>
                </div>
              )}

              <div className="tsm-payment-header">
                <div className="tsm-payment-label">You&apos;re about to subscribe to</div>
                <div className="tsm-payment-tier-name">{tier.name}</div>
                <div className="tsm-payment-tier-price">
                  {selectedDisplay?.priceLabel || tier.priceLabel}
                  <span className="tsm-payment-tier-detail">/month, cancel anytime</span>
                </div>
              </div>

              <div className="tsm-payment-form">
                <div className="tsm-payment-form-title">Payment method</div>
                {!clientSecret || !stripePromise ? (
                  <div className="tsm-stripe-placeholder">
                    {!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY && !publishableKey
                      ? "Add REACT_APP_STRIPE_PUBLISHABLE_KEY to enable Stripe Payment Element."
                      : "Loading secure payment form…"}
                  </div>
                ) : (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: "stripe",
                        variables: {
                          colorPrimary: "#C4790A",
                          borderRadius: "8px",
                          fontFamily: "Inter, sans-serif",
                        },
                      },
                    }}
                  >
                    <PaymentForm
                      email={currentUser?.email}
                      onReady={onPaymentReady}
                      onStripeRefs={onStripeRefs}
                    />
                  </Elements>
                )}
              </div>

              <div className="tsm-billing-info">
                <strong>Billing Details:</strong>
                <br />
                • Charged <strong>{selectedDisplay?.priceMonthly || `${tier.priceLabel}/mo`}</strong>, starting today
                <br />
                • Renews automatically each month
                <br />
                • You can cancel anytime, no questions asked
                <br />
                • Your subscription gives you access until the end of the current billing cycle
              </div>
            </div>
          )}

          {error && <div className="tsm-error">{error}</div>}
        </div>

        <div className="tsm-footer">
          {phase === "payment" ? (
            <button
              type="button"
              className="tsm-btn-secondary"
              disabled={busy}
              onClick={handleBack}
            >
              ← Back
            </button>
          ) : (
            <button
              type="button"
              className="tsm-btn-secondary"
              disabled={busy}
              onClick={handleDismiss}
            >
              Sorry, maybe another time
            </button>
          )}
          <button
            type="button"
            className="tsm-btn-primary"
            disabled={primaryDisabled}
            onClick={handlePrimary}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
